import { memo, useEffect, useRef, useState, type FormEvent, type RefObject } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { type AgentTransport } from "../agent/graph";
import { useAgentSession, type Item } from "../agent/AgentSessionContext";
import {
  createSession,
  getSession,
  updateSession,
} from "../agent/sessionsApi";
import { UiToolRenderer } from "../components/chat/UiToolRenderer";
import { Markdown } from "../components/chat/Markdown";

// The agent runs a default model server-side; we record a sensible default with
// each save so the saved-chats list can show which model produced the chat.
const DEFAULT_MODEL = "claude-opus-4-8";

// /ask (spec-9, ac-30): the full-page, read-only analytics agent. The operator
// asks a question; the client ReAct loop (agent/graph.ts) drives it — streaming
// the model's prose into the transcript as it arrives, proxying server tools, and
// rendering UI tools (charts, tables, callouts…) inline. Interactive widgets
// (choices / action buttons) pause the loop until the operator picks (ac-28/29).
// The chat session lives in AgentSessionContext (hoisted above the router), so it
// survives navigating away and back — in memory only, never persisted (ac-34).
//
// The input draft is LOCAL state (not in the shared session): keystrokes must not
// re-render the provider's whole subtree, and the transcript is memoized so typing
// never re-renders the (potentially heavy) message + chart list.

export function AskPage({
  transport,
}: {
  // Injectable so tests can drive the loop without the network / a provider.
  transport?: AgentTransport;
}) {
  const {
    items,
    busy,
    error,
    submit,
    respond,
    stop,
    clear,
    snapshot,
    load,
    loadedSessionId,
    loadedSavedBy,
    loadedName,
  } = useAgentSession(transport);
  const [draft, setDraft] = useState("");
  // The re-save confirmation (Update vs Save-as-new) for a loaded chat.
  const [resaveOpen, setResaveOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  const { sessionId } = useParams();
  const navigate = useNavigate();
  // Guards against re-fetching the same session on every render.
  const loadedFor = useRef<string | null>(null);

  // t-5: a per-chat URL (/ask/:sessionId) rehydrates that saved chat — GET it,
  // then load() so the full transcript re-renders and history resumes. Bare /ask
  // is left untouched (a fresh, in-memory chat that survives navigation — ac-34).
  useEffect(() => {
    if (!sessionId || loadedFor.current === sessionId) return;
    let cancelled = false;
    getSession(sessionId)
      .then((s) => {
        if (cancelled) return;
        // Claim the id only once we actually apply the result. Pre-claiming it
        // before the fetch breaks under StrictMode's mount→cleanup→mount: the
        // first request would be cancelled by the cleanup while the guard
        // suppressed the second mount's refetch, leaving the chat unloaded.
        loadedFor.current = sessionId;
        load(s.messages, s.uiItems, {
          id: s.id,
          savedBy: s.savedByEmail,
          name: s.name,
        });
      })
      .catch(() => {
        if (!cancelled) setSaveError("Could not load that saved chat.");
      });
    return () => {
      cancelled = true;
    };
  }, [sessionId, load]);

  // POST a new saved chat under `name`, then adopt it as the loaded chat and move
  // to its stable, shareable URL.
  const createAs = async (name: string) => {
    const snap = snapshot();
    setSaving(true);
    setSaveError(null);
    try {
      const meta = await createSession({
        name,
        messages: snap.messages,
        uiItems: snap.items,
        model: DEFAULT_MODEL,
      });
      loadedFor.current = meta.id;
      load(snap.messages, snap.items, {
        id: meta.id,
        savedBy: meta.savedByEmail,
        name: meta.name,
      });
      navigate(`/ask/${meta.id}`);
    } catch {
      setSaveError("Could not save this chat.");
    } finally {
      setSaving(false);
    }
  };

  // The "Save" control. A never-saved chat just prompts for a name; a chat loaded
  // from an existing session opens the Update / Save-as-new confirmation (t-3).
  const onSave = () => {
    setSaveError(null);
    if (loadedSessionId) {
      setResaveOpen(true);
      return;
    }
    const name = window.prompt("Name this chat")?.trim();
    if (name) void createAs(name);
  };

  // Update: PUT over the loaded session (overwrites whoever's chat it is).
  const onUpdate = async () => {
    setResaveOpen(false);
    if (!loadedSessionId) return;
    const snap = snapshot();
    setSaving(true);
    setSaveError(null);
    try {
      await updateSession(loadedSessionId, {
        name: loadedName ?? "",
        messages: snap.messages,
        uiItems: snap.items,
        model: DEFAULT_MODEL,
      });
    } catch {
      setSaveError("Could not update this chat.");
    } finally {
      setSaving(false);
    }
  };

  // Save as new: prompt for a fresh name and POST — the original is left untouched.
  const onSaveAsNew = () => {
    setResaveOpen(false);
    const name = window.prompt("Name the new chat")?.trim();
    if (name) void createAs(name);
  };

  const bottomRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [items, busy]);

  // Esc stops the current execution (the in-flight completion / tool call, or a
  // paused interactive widget). Only while a turn is running.
  useEffect(() => {
    if (!busy) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.preventDefault();
        stop();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [busy, stop]);

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    const q = draft;
    setDraft("");
    void submit(q);
  };

  return (
    <div className="flex h-full flex-col" data-testid="ask-page">
      <header className="flex items-start justify-between gap-4 border-b border-edge px-8 py-6">
        <div>
          <h1 className="text-2xl text-heading">Ask God</h1>
          <p className="text-sm text-muted" data-testid="ask-description">
            Read-only analytics over the product database, application logs, and
            Postmark email deliverability — ask in plain English.
          </p>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          <Link
            to="/ask/saved"
            data-testid="ask-saved-link"
            className="rounded-lg border border-edge px-3 py-1.5 text-sm text-secondary transition-colors hover:bg-surface"
          >
            Saved chats
          </Link>
          <button
            type="button"
            data-testid="ask-save"
            onClick={onSave}
            disabled={busy || saving || items.length === 0}
            className="rounded-lg border border-edge px-3 py-1.5 text-sm text-secondary transition-colors hover:bg-surface disabled:cursor-not-allowed disabled:opacity-40"
          >
            Save
          </button>
          <button
            type="button"
            data-testid="ask-clear"
            onClick={clear}
            disabled={busy || items.length === 0}
            className="rounded-lg border border-edge px-3 py-1.5 text-sm text-secondary transition-colors hover:bg-surface disabled:cursor-not-allowed disabled:opacity-40"
          >
            Clear
          </button>
        </div>
      </header>

      {resaveOpen && (
        <div
          data-testid="ask-resave-dialog"
          role="dialog"
          aria-modal="true"
          className="border-b border-edge bg-surface px-8 py-4"
        >
          <p className="text-sm text-secondary" data-testid="ask-resave-message">
            This chat was saved by{" "}
            <span className="font-medium text-primary">
              {loadedSavedBy ?? "someone"}
            </span>
            {loadedName ? ` as “${loadedName}”` : ""}. Update it, or save a copy as
            a new chat?
          </p>
          <div className="mt-3 flex items-center gap-2">
            <button
              type="button"
              data-testid="ask-resave-update"
              onClick={() => void onUpdate()}
              className="rounded-lg bg-accent px-3 py-1.5 text-sm font-medium text-on-accent transition-colors hover:bg-accent-hover"
            >
              Update
            </button>
            <button
              type="button"
              data-testid="ask-resave-new"
              onClick={onSaveAsNew}
              className="rounded-lg border border-edge px-3 py-1.5 text-sm text-secondary transition-colors hover:bg-surface"
            >
              Save as new
            </button>
            <button
              type="button"
              data-testid="ask-resave-cancel"
              onClick={() => setResaveOpen(false)}
              className="rounded-lg px-3 py-1.5 text-sm text-muted transition-colors hover:bg-surface"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {saveError && (
        <div
          data-testid="ask-save-error"
          className="border-b border-status-danger-border bg-status-danger-bg px-8 py-2 text-sm text-status-danger-text"
        >
          {saveError}
        </div>
      )}

      <Transcript
        items={items}
        busy={busy}
        error={error}
        respond={respond}
        bottomRef={bottomRef}
      />

      <form
        data-testid="ask-form"
        className="border-t border-edge px-8 py-4"
        onSubmit={onSubmit}
      >
        <div className="mx-auto flex max-w-3xl items-center gap-3">
          <input
            type="text"
            data-testid="ask-input"
            value={draft}
            disabled={busy}
            placeholder="Ask a question about the data…"
            onChange={(e) => setDraft(e.target.value)}
            className="flex-1 rounded-lg border border-edge bg-input px-3 py-2 text-sm text-primary outline-none focus:border-accent disabled:opacity-60"
          />
          {busy ? (
            <button
              type="button"
              data-testid="ask-stop"
              onClick={stop}
              title="Stop (Esc)"
              className="rounded-lg border border-edge px-4 py-2 text-sm font-medium text-secondary transition-colors hover:bg-surface"
            >
              Stop
            </button>
          ) : (
            <button
              type="submit"
              data-testid="ask-send"
              disabled={!draft.trim()}
              className="rounded-lg bg-accent px-4 py-2 text-sm font-medium text-on-accent transition-colors hover:bg-accent-hover disabled:cursor-not-allowed disabled:opacity-50"
            >
              Send
            </button>
          )}
        </div>
      </form>
    </div>
  );
}

// The transcript is memoized so it only re-renders when the conversation actually
// changes — not on every keystroke in the input (which is local AskPage state).
const Transcript = memo(function Transcript({
  items,
  busy,
  error,
  respond,
  bottomRef,
}: {
  items: Item[];
  busy: boolean;
  error: string | null;
  respond: (blockId: string, value: string) => void;
  bottomRef: RefObject<HTMLDivElement | null>;
}) {
  return (
    <div className="flex-1 overflow-y-auto px-8 py-6" data-testid="ask-transcript">
      <div className="mx-auto max-w-3xl space-y-4">
        {items.length === 0 && (
          <p className="text-sm text-muted" data-testid="ask-empty">
            Try: "Which orgs grew the most this month?", "Show MCP traffic by org
            as a chart." or "What's our email bounce and spam-complaint rate this
            week, and which addresses are bouncing?"
          </p>
        )}
        {items.map((item) => {
          if (item.kind === "user") {
            return (
              <div key={item.id} className="flex justify-end">
                <div
                  data-testid="ask-user"
                  className="max-w-[80%] rounded-2xl bg-accent px-4 py-2 text-sm text-on-accent"
                >
                  {item.text}
                </div>
              </div>
            );
          }
          if (item.kind === "text") {
            return (
              <div key={item.id} data-testid="ask-assistant">
                <Markdown>{item.text}</Markdown>
              </div>
            );
          }
          if (item.kind === "servertool") {
            return (
              <details
                key={item.id}
                data-testid="ask-servertool"
                data-tool={item.name}
                className="rounded-lg border border-edge bg-surface px-3 py-2 text-xs"
              >
                <summary className="cursor-pointer text-muted">
                  Ran <span className="font-medium text-secondary">{item.name}</span>
                </summary>
                {item.preview && (
                  <pre className="mt-2 overflow-x-auto whitespace-pre-wrap text-[11px] text-muted">
                    {item.preview}
                  </pre>
                )}
              </details>
            );
          }
          return (
            <div key={item.id} data-testid="ask-tool">
              <UiToolRenderer
                block={item.block}
                resolved={item.resolved}
                onRespond={
                  item.interactive ? (value) => respond(item.id, value) : undefined
                }
              />
            </div>
          );
        })}
        {busy && (
          <p className="text-sm text-muted" data-testid="ask-thinking">
            Thinking…
          </p>
        )}
        {error && (
          <p
            data-testid="ask-error"
            className="rounded-lg border border-status-danger-border bg-status-danger-bg px-3 py-2 text-sm text-status-danger-text"
          >
            {error}
          </p>
        )}
        <div ref={bottomRef} />
      </div>
    </div>
  );
});
